/** Campaigns page — stat cards summary + grid of campaign cards.
 *
 * The summary cards roll up across the whole campaign list (total,
 * active, sessions tracked). The grid below shows one card per
 * campaign with the new server-side stats: session_count + closed +
 * live + last_session_at.
 *
 * The "create campaign" inline form stays in CampaignList for now —
 * we leave that module's mutation logic in place and just wrap the
 * new presentation around it.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AppLayout } from "@/components/AppLayout";
import { Pagination } from "@/components/Pagination";
import { PermissionGate } from "@/components/PermissionGate";
import { StatCard } from "@/components/StatCard";
import { formatRelative } from "@/lib/format";
import type { Campaign } from "@/lib/types";
import { listCampaigns } from "@/modules/campaigns/api";

// The stat cards roll up the WHOLE fetched list, so we keep fetching the
// full bounded page (max 200) and paginate the grid client-side.
const GRID_PAGE_SIZE = 12;

export default function CampaignsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);

  // Campaigns per org are few (tens, not thousands); fetch the full
  // bounded page (max 200) so the stat cards stay complete while the
  // endpoint itself is capped. `count` gives the true total.
  const q = useQuery({
    queryKey: ["campaigns", includeArchived],
    queryFn: () => listCampaigns({ includeArchived, pageSize: 200 }),
  });

  const campaigns = q.data?.results ?? [];
  const totalCampaigns = q.data?.count ?? 0;
  const activeCampaigns = campaigns.filter((c) => !c.archived_at).length;
  const totalSessions = campaigns.reduce(
    (sum, c) => sum + (c.session_count || 0),
    0,
  );
  // Grid is paginated client-side over the already-fetched list — the stat
  // cards above still reflect the full set.
  const pagedCampaigns = campaigns.slice(
    (page - 1) * GRID_PAGE_SIZE,
    page * GRID_PAGE_SIZE,
  );

  return (
    <AppLayout
      title={t("campaignsPage.title")}
      description={t("campaignsPage.description")}
      headerAction={
        <PermissionGate perm="campaign.create">
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate("/campaigns/new")}
          >
            {t("campaignsPage.newCampaign")}
          </button>
        </PermissionGate>
      }
    >
      <PermissionGate
        perm="campaign.read"
        fallback={
          <p className="card text-sm text-nb-text-2">
            {t("admin.noPermission")}
          </p>
        }
      >
        {/* Stats summary */}
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            label={t("campaignsPage.statTotal")}
            value={totalCampaigns}
            tone="info"
          />
          <StatCard
            label={t("campaignsPage.statActive")}
            value={activeCampaigns}
            tone="ok"
          />
          <StatCard
            label={t("campaignsPage.statSessionsTotal")}
            value={totalSessions}
            tone="warn"
          />
        </section>

        {/* Archived toggle */}
        <div className="flex items-center justify-end">
          <label className="flex items-center gap-2 text-sm text-nb-text-2">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => {
                setIncludeArchived(e.target.checked);
                setPage(1); // filter changed the set → back to first page
              }}
            />
            {t("ui.campaignList.includeArchived")}
          </label>
        </div>

        {/* Campaign cards grid */}
        {q.isLoading && (
          <p className="card text-sm text-nb-text-2">{t("dashboard.loading")}</p>
        )}
        {/* Checked BEFORE the empty state — `campaigns` is also `[]` on a
            fetch failure, and the old check couldn't tell the two apart:
            an operator whose campaigns failed to load saw "No campaigns
            yet. Click..." — actively wrong, not just uninformative. */}
        {q.isError && !q.isLoading && (
          <p className="card text-sm text-cvp-danger">
            {t("campaignsPage.loadError", "Could not load campaigns.")}
          </p>
        )}
        {campaigns.length === 0 && !q.isLoading && !q.isError && (
          <div className="card text-sm text-nb-text-2">
            {t(
              "campaignsPage.empty",
              'No campaigns yet. Click "{{newCampaign}}" to create your first one.',
              { newCampaign: t("campaignsPage.newCampaign") },
            )}
          </div>
        )}
        {campaigns.length > 0 && (
          <>
            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {pagedCampaigns.map((c) => (
                <CampaignCard key={c.id} campaign={c} t={t} />
              ))}
            </section>
            <Pagination
              page={page}
              pageSize={GRID_PAGE_SIZE}
              count={campaigns.length}
              onPage={setPage}
            />
          </>
        )}
      </PermissionGate>
    </AppLayout>
  );
}


function CampaignCard({
  campaign,
  t,
}: {
  campaign: Campaign;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  return (
    <Link
      to={`/campaigns/${campaign.id}`}
      className="card group block transition-colors hover:border-nb-border"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-nb-text group-hover:text-nb-mint">
            {campaign.name}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {campaign.is_ended && (
              <span className="inline-block rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300">
                {t("campaignsPage.ended")}
              </span>
            )}
            {campaign.is_archived && (
              <span className="inline-block rounded bg-slate-700/40 px-1.5 py-0.5 text-[10px] text-nb-text-2">
                {t("campaignsPage.archived")}
              </span>
            )}
          </div>
        </div>
      </div>
      {campaign.description && (
        <p className="mt-2 line-clamp-2 text-sm text-nb-text-2">
          {campaign.description}
        </p>
      )}

      {/* Planning window — only renders when at least one date is set. */}
      {(campaign.start_date || campaign.end_date) && (
        <div className="mt-3 text-xs text-nb-text-2">
          {campaign.start_date && campaign.end_date
            ? t("campaignsPage.labelDates", {
                start: campaign.start_date,
                end: campaign.end_date,
              })
            : campaign.start_date
              ? `${t("campaignsPage.labelStartDate")}: ${campaign.start_date}`
              : `${t("campaignsPage.labelEndDate")}: ${campaign.end_date}`}
        </div>
      )}

      {/* Progress bar — server-computed (time-elapsed when both dates
          set, else closed/total sessions). Null = nothing to show. */}
      {campaign.progress !== null && (
        <div className="mt-3 space-y-1.5">
          <div className="flex items-baseline justify-between text-[10px] uppercase tracking-wider text-nb-text-3">
            <span>{t("campaignsPage.labelProgress")}</span>
            <span className="font-mono text-nb-text">
              {Math.round(campaign.progress * 100)}%
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-nb-surface">
            <div
              className={
                "h-full rounded-full transition-all " +
                (campaign.is_ended ? "bg-emerald-500" : "bg-nb-mint")
              }
              style={{ width: `${Math.round(campaign.progress * 100)}%` }}
            />
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-nb-text-2">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
          {t("campaignsPage.labelSessions", { count: campaign.session_count })}
        </span>
        {campaign.closed_count !== undefined && campaign.closed_count > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {t("campaignsPage.labelClosed", { count: campaign.closed_count })}
          </span>
        )}
        {campaign.live_count !== undefined && campaign.live_count > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
            {t("campaignsPage.labelLive", { count: campaign.live_count })}
          </span>
        )}
      </div>

      <div className="mt-4 border-t border-nb-border pt-3 text-xs text-nb-text-3">
        {t("campaignsPage.labelLastActivity")}:{" "}
        <span className="text-nb-text">
          {campaign.last_session_at
            ? formatRelative(campaign.last_session_at)
            : t("campaignsPage.neverUsed")}
        </span>
      </div>
    </Link>
  );
}
